import { API_BASE_URL } from "src/config/api";
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  Box,
  TextField,
} from '@mui/material';
import axios from 'axios';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

const ContactSection = styled.section`
  background: linear-gradient(to right, #e6f0ff, #fff);
  padding-top: 60px;
  padding-bottom: 80px; 
  min-height: 100vh; 
  box-sizing: border-box;
`;



const ContactHeader = styled.div`
  text-align: center;
  margin-bottom: 90px;
`;

const FormContainer = styled(Box)`
  max-width: 750px;
  margin: 0 auto;
  padding: 40px;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.14);
`;

function ContactUs() {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [refreshForm, setRefreshForm] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'name') {
      if (!/^[a-zA-Z\s]*$/.test(value)) {
        setError('Name should only contain letters and spaces.');
      } else {
        const lettersCount = value.replace(/\s/g, '').length;
        if (lettersCount <= 3) {
          setError('Name should have more than three letters.');
        } else {
          setError('');
        }
      }
    } else {
      setError('');
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      const apiUrl = `${API_BASE_URL}/contactForm`;
      const response = await axios.post(apiUrl, formData);

      if (response.status === 200) {
        setError('');
        toast.success(response.data.message);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
        setRefreshForm((prev) => !prev);
      } else {
        toast.error(response.data.message || 'Submission failed.');
      }
    } catch (error) {
      toast.error('Server Not Found', {
        position: toast.POSITION.TOP_RIGHT,
      });
      setError('Error Submitting Data');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContactSection>
      <ContactHeader>
        <Typography
          component="h1"
          variant="h2"
          sx={{
            color: '#002244',
            fontWeight: 700,
            textAlign: 'center',
            mb: 3,
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          Contact WatchNod - Get Support & Assistance
        </Typography>
        <Typography variant="h6" color="#004080">
          Have questions or need assistance? Fill out the form below, and our team will get back to you promptly.
        </Typography>
      </ContactHeader>

      <FormContainer component="form" key={refreshForm} onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Phone Number"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Message"
              name="message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleInputChange}
              required
            />
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : null
              }
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Send Message'}
            </Button>
          </Grid>
        </Grid>
      </FormContainer>
    </ContactSection>
  );
}

export default ContactUs;
